/**
 * DocumentDetailModal — Modal de solo lectura para ver el detalle de un documento de venta.
 * Formulario vertical con secciones: Encabezado, Receptor, Líneas, Referencias, Personalizados.
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, Info, Code2, Copy, Check, ChevronDown, ChevronRight, Download, Eye, Paperclip, Mail, RefreshCw, AlertTriangle, Pencil, Loader2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { fmtDate, fmtDateTime, formatRut } from '@/lib/utils'
import { useTenantLocale, formatMoneyForTenant, useTenantCurrency } from '@/lib/locale'
import type { SaleDocument, CustomerListItem, EmissionLog, VoucherPreviewResponse, DocumentPersonalizado } from '@/modules/sales/hooks/useSales'
import { useVoucherPreview, useUpdateDocumentMetadata, salesKeys } from '@/modules/sales/hooks/useSales'
import apiClient from '@/lib/apiClient'
import { displayLabelCls as labelCls } from '@/lib/formStyles'
import { XmlViewer, type XmlVariant } from '@/components/XmlViewer'
import CustomFieldsForm, { mergeCustomFields } from '@/components/CustomFieldsForm'
import { useGlobalCustomFields, useTenantTax } from '@/modules/config/hooks/useConfig'
import { StatusBadge, DTE_STATUS, PAYMENT_STATUS } from '@/components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { TextField } from '@/components/ds/TextField'
import { Modal } from '@/components/ds/Modal'

interface DocumentDetailModalProps {
  open: boolean
  onClose: () => void
  doc: SaleDocument | null
  customers: CustomerListItem[]
  loading?: boolean
  error?: unknown
}

const fieldCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content min-h-[38px]'
const sectionTitleCls = 'text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3'

/*
 * El mapa de estados vivía acá, copiado del listado y del dashboard — y los
 * TRES discrepaban entre sí: el mismo documento se veía de otro color según la
 * pantalla, y `pending_send` ni siquiera existía en el dashboard. Ahora sale de
 * `DTE_STATUS`, que es el catálogo único verificado contra
 * `SaleDocument.STATUS_CHOICES`.
 */

const EVENT_LABELS: Record<string, string> = {
  started:        'Iniciado',
  xml_generated:  'XML Generado',
  xml_signed:     'XML Firmado',
  sent:           'Enviado al Facturador',
  status_queried: 'Estado Consultado',
  accepted:       'Aceptado',
  rejected:       'Rechazado',
  error:          'Error',
  email_sent:     'Enviado por Correo',
}

function LogIcon({ status }: { status: EmissionLog['status'] }) {
  if (status === 'success') return <CheckCircle2 size={14} className="text-success-solid shrink-0 mt-0.5" />
  if (status === 'error')   return <XCircle size={14} className="text-danger-solid shrink-0 mt-0.5" />
  return <Info size={14} className="text-content-subtle shrink-0 mt-0.5" />
}

const DOC_TYPE_LABELS: Record<string, string> = {
  FE: 'Factura Electrónica',
  BE: 'Boleta Electrónica',
  NC: 'Nota de Crédito',
  ND: 'Nota de Débito',
  FC: 'Factura de Compra',
}

const COD_REF_LABELS: Record<string, string> = {
  '1': 'Anula documento',
  '2': 'Corrige texto',
  '3': 'Corrige monto',
}

const DISTRIBUTION_SOURCE_LABELS: Record<string, string> = {
  ce_lookup: 'Padrón SII de Contribuyentes Electrónicos',
  correo_recep_xml: 'CorreoRecep del XML del DTE',
  customer_email: 'Email del cliente (CRM)',
  manual: 'Asignación manual',
}

function DistributionStatusPanel({ doc, locale }: { doc: SaleDocument; locale: string }) {
  const ld = (doc.localization_data ?? {}) as Record<string, unknown>
  const sentAt = ld.distributed_to_receiver_at as string | undefined
  const email = ld.distribution_email as string | undefined
  const source = ld.distribution_source as string | undefined
  const error = ld.distribution_error as string | undefined
  const [busy, setBusy] = useState(false)
  const [justQueuedAt, setJustQueuedAt] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const qc = useQueryClient()

  // El backend encola una task de Celery — la distribución es async. Mostramos
  // un estado "Encolado" durante 8s post-click + invalidamos la query para
  // que cuando Celery actualice localization_data, el panel pase a "Distribuido".
  const queuedRecently = justQueuedAt !== null

  useEffect(() => {
    if (justQueuedAt === null) return
    // Re-fetch a los 4s y 8s para capturar el cambio de localization_data.
    const t1 = setTimeout(() => qc.invalidateQueries({ queryKey: salesKeys.document(doc.id) }), 4000)
    const t2 = setTimeout(() => {
      qc.invalidateQueries({ queryKey: salesKeys.document(doc.id) })
      setJustQueuedAt(null)
    }, 8000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [justQueuedAt, doc.id, qc])

  async function redistribute() {
    setActionError(null)
    setBusy(true)
    try {
      await apiClient.post(`/sales/documents/${doc.id}/distribute-to-receiver/`, { force: true })
      setJustQueuedAt(Date.now())
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      setActionError(err.response?.data?.error?.message ?? 'No se pudo encolar la distribución. Intentalo de nuevo en unos segundos.')
    } finally {
      setBusy(false)
    }
  }

  const buttonDisabled = busy || queuedRecently
  const buttonLabel = busy ? 'Encolando…' : queuedRecently ? 'Encolado ✓' : 'Enviar ahora'

  const feedback = (
    <>
      {queuedRecently && (
        <div className="bg-success-surface border border-success-border rounded-lg p-3 flex items-start gap-2">
          <CheckCircle2 size={14} className="text-success-content-soft mt-0.5 shrink-0" />
          <p className="text-xs text-success-content-strong">
            {busy ? 'Encolando…' : 'Encolado para envío al receptor. El proceso corre en segundo plano y se actualizará automáticamente en unos segundos.'}
          </p>
        </div>
      )}
      {actionError && (
        <div className="bg-danger-surface border border-danger-border rounded-lg p-3 flex items-start gap-2">
          <XCircle size={14} className="text-danger-content-soft mt-0.5 shrink-0" />
          <p className="text-xs text-danger-content-strong">{actionError}</p>
        </div>
      )}
    </>
  )

  if (sentAt) {
    return (
      <div className="space-y-2">
      <div className="bg-success-surface border border-success-border rounded-lg p-3 flex items-start gap-3">
        <Mail size={16} className="text-success-content-soft mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-success-content-strong">
            Distribuido al receptor
          </p>
          <p className="text-xs text-success-content mt-0.5 break-all">
            {email} · {fmtDateTime(sentAt, locale)}
          </p>
          {source && (
            <p className="text-xs text-emerald-700/70 dark:text-emerald-300/70 mt-0.5">
              Fuente: {DISTRIBUTION_SOURCE_LABELS[source] ?? source}
            </p>
          )}
        </div>
        <button
          onClick={redistribute}
          disabled={buttonDisabled}
          title={queuedRecently ? 'Reenvío encolado — esperá a que se procese' : 'Reenviar al receptor'}
          className="text-success-content hover:bg-success-surface-strong rounded p-1 disabled:opacity-50"
        >
          {queuedRecently
            ? <Check size={14} />
            : <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />}
        </button>
      </div>
      {feedback}
      </div>
    )
  }

  if (error === 'no_recipient_email') {
    return (
      <div className="space-y-2">
      <div className="bg-warning-surface border border-warning-border rounded-lg p-3 flex items-start gap-3">
        <AlertTriangle size={16} className="text-warning-content-soft mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-warning-content-strong">
            No distribuido — sin email del receptor
          </p>
          <p className="text-xs text-warning-content mt-0.5">
            El receptor no está en el padrón de Contribuyentes Electrónicos del SII, el XML no
            trae CorreoRecep y el cliente no tiene email en el CRM. Cargá un email al cliente y
            reintentá.
          </p>
        </div>
        <button
          onClick={redistribute}
          disabled={buttonDisabled}
          className="text-warning-content hover:bg-warning-surface-strong rounded p-1 disabled:opacity-50 text-xs"
        >
          {queuedRecently
            ? <Check size={14} />
            : <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />}
        </button>
      </div>
      {feedback}
      </div>
    )
  }

  // Aceptado pero todavía no encolado o se procesa async — mostrar pendiente
  return (
    <div className="space-y-2">
      <div className="bg-soft border border-divider rounded-lg p-3 flex items-center gap-3">
        <Mail size={14} className="text-content-muted" />
        <span className="text-xs text-content-muted">
          Distribución al receptor pendiente (se enviará automáticamente)
        </span>
        <button
          onClick={redistribute}
          disabled={buttonDisabled}
          className="ml-auto text-xs text-primary-700 hover:underline disabled:opacity-50 disabled:no-underline"
        >
          {buttonLabel}
        </button>
      </div>
      {feedback}
    </div>
  )
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className={fieldCls}>{value || '—'}</div>
    </div>
  )
}


export function DocumentDetailModal({
  open,
  onClose,
  doc,
  customers,
  loading = false,
  error,
}: DocumentDetailModalProps) {
  const locale = useTenantLocale()
  const tax = useTenantTax()
  const baseCurrency = useTenantCurrency()
  const [linkCopied, setLinkCopied] = useState(false)
  const [emissionLogsOpen, setEmissionLogsOpen] = useState(false)
  const [voucherOpen, setVoucherOpen] = useState(false)
  const { data: voucherPreview, isLoading: isLoadingVoucher } = useVoucherPreview(
    doc?.id ?? null,
    voucherOpen && open,
  )

  // Edición de campos NO contables (personalizados, observaciones) en cualquier estado.
  // La lista de campos sale de las DEFINICIONES del tenant, no del JSON del
  // documento: el JSON guarda solo lo que se llenó, así que los campos creados
  // después del documento (o dejados vacíos) no aparecían y no había dónde
  // escribirlos.
  const updateMeta = useUpdateDocumentMetadata(doc?.id ?? '')
  const { data: ventasFields = [] } = useGlobalCustomFields(true, 'ventas')
  const [editingMeta, setEditingMeta] = useState(false)
  const [draftNotes, setDraftNotes] = useState('')
  const [draftPers, setDraftPers] = useState<DocumentPersonalizado[]>([])

  useEffect(() => {
    if (!open) { setVoucherOpen(false); setEmissionLogsOpen(false); setEditingMeta(false) }
    if (open && doc) {
      setDraftNotes(doc.notes ?? '')
      setDraftPers(mergeCustomFields(ventasFields, doc.personalizados) as DocumentPersonalizado[])
    }
  }, [open, doc, ventasFields])

  const metaVisibles = mergeCustomFields(ventasFields, doc?.personalizados) as DocumentPersonalizado[]
  const nombresDefinidos = new Set(ventasFields.map(f => f.nombre))
  const metaExtras = draftPers.filter(p => !nombresDefinidos.has(p.nombre))


  if (!open) return null

  // Mientras carga o si falló el fetch: shell con feedback en vez de invisible.
  if (!doc) {
    const errMsg = error
      ? (() => {
          const e = error as { response?: { status?: number; data?: { error?: { message?: string } } }; message?: string }
          const status = e?.response?.status
          const apiMsg = e?.response?.data?.error?.message
          return apiMsg || (status ? `Error ${status} al cargar el documento` : (e?.message || 'No se pudo cargar el documento'))
        })()
      : null
    return (
      <Modal
        open
        onClose={onClose}
        size="sm"
        title="Documento de venta"
      >
        {loading && !errMsg && (
          <div className="flex items-center gap-3 text-content-muted">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Cargando documento…</span>
          </div>
        )}
        {errMsg && (
          <div className="flex items-start gap-3">
            <XCircle size={20} className="text-danger-solid shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-content">No se pudo abrir el documento</p>
              <p className="text-xs text-content-muted mt-1">{errMsg}</p>
            </div>
          </div>
        )}
      </Modal>
    )
  }

  const customer = customers.find(c => c.id === doc.customer)
  const rotuloDelEstado = DTE_STATUS[doc.status]?.label ?? doc.status
  const folio = doc.localization_data?.['document_number'] as string | undefined

  const formatCLP = (val: string) =>
    formatMoneyForTenant(parseFloat(val) || 0)

  // Precio unitario: preserva hasta 3 decimales (sin ceros sobrantes) — el monto
  // puede tener decimales (ej. tasa 894,99), a diferencia de los totales, que
  // van en la moneda base del tenant.
  const formatUnitPrice = (val: string) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: baseCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    }).format(parseFloat(val) || 0)

  // Cantidad: hasta 3 decimales sin ceros sobrantes (1798.000 → 1.798; 116.661 → 116,661).
  const formatQty = (val: string) =>
    new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    }).format(parseFloat(val) || 0)

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
      <span>
        {DOC_TYPE_LABELS[doc.document_type] || doc.document_type}
        {folio ? ` #${folio}` : ''}
      </span>
    }
      subtitle={<StatusBadge status={doc.status} catalog={DTE_STATUS} />}
      actions={
        <>
          <div className="mr-auto flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                const link = `${window.location.origin}/sales/documents?open=${doc.id}`
                navigator.clipboard?.writeText(link)
                setLinkCopied(true)
                setTimeout(() => setLinkCopied(false), 2000)
              }}
              title="Copiar enlace al documento"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-muted bg-soft border border-divider rounded-lg hover:bg-canvas transition-colors"
            >
              {linkCopied ? <Check size={13} className="text-success-solid" /> : <Copy size={13} />}
              {linkCopied ? 'Copiado' : 'Copiar enlace'}
            </button>
            {doc.status === 'accepted' && (
              <button
                onClick={async () => {
                  try {
                    const res = await apiClient.get(`/sales/documents/${doc.id}/pdf/`, { responseType: 'blob' })
                    const url = URL.createObjectURL(res.data)
                    window.open(url, '_blank')
                  } catch { /* ignore */ }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-success-content bg-success-surface border border-success-border rounded-lg hover:bg-success-surface-strong transition-colors"
              >
                <Download size={13} />
                PDF
              </button>
            )}
            {doc.status !== 'accepted' && (
              <button
                onClick={async () => {
                  try {
                    const res = await apiClient.get(`/sales/documents/${doc.id}/pdf/`, { responseType: 'blob' })
                    const url = URL.createObjectURL(res.data)
                    window.open(url, '_blank')
                  } catch { /* ignore */ }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-muted bg-soft border border-divider rounded-lg hover:bg-canvas transition-colors"
                title="Vista previa del documento (marcado como borrador, sin validez tributaria)"
              >
                <Download size={13} />
                PDF
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
          >
            Cerrar
          </button>
        </>
      }
    >
      <div className="space-y-6">

        {/* ── Encabezado ── */}
        <div>
          <p className={sectionTitleCls}>Encabezado</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tipo de documento" value={DOC_TYPE_LABELS[doc.document_type]} />
            <Field label="Estado" value={rotuloDelEstado} />
            <Field label="Fecha de emisión" value={fmtDate(doc.issue_date, locale)} />
            <Field label="Fecha de vencimiento" value={fmtDate(doc.due_date, locale)} />
            <Field label="Forma de pago" value={
              (doc as any).payment_method === 'credito'
                ? `Crédito (${(doc as any).payment_term_days || 0} días)`
                : 'Contado'
            } />
            {folio && <Field label="Folio" value={folio} />}
            {(doc as any).seller_name && <Field label="Vendedor" value={(doc as any).seller_name} />}
          </div>
        </div>

        {/* ── Distribución al receptor (SII Chile) ── */}
        {doc.status === 'accepted' && (
          <DistributionStatusPanel doc={doc} locale={locale} />
        )}

        {/* ── Retry info (pending_send / rejected con intentos) ── */}
        {(doc.status === 'pending_send' || (doc.status === 'rejected' && !!doc.localization_data?.send_attempts)) && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 space-y-1">
            <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-200">Información de reenvío</p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300">
              Intentos: {String(doc.localization_data?.send_attempts ?? 0)}/18
            </p>
            {!!doc.localization_data?.last_send_error && (
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Último error: {String(doc.localization_data.last_send_error)}
              </p>
            )}
            {!!doc.localization_data?.next_retry_at && (
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Próximo reintento: {fmtDateTime(String(doc.localization_data.next_retry_at), locale)}
              </p>
            )}
          </div>
        )}

        {/* ── Receptor ── */}
        <div>
          <p className={sectionTitleCls}>Receptor</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="RUT / ID Tributario" value={formatRut(customer?.tax_id)} />
            <Field label="Razón Social" value={customer?.legal_name || doc.customer_name} />
            <Field label="Giro Comercial" value={customer?.giro} />
            <Field label="Dirección" value={customer?.address_street} />
            <Field label="Comuna" value={customer?.address_commune} />
            <Field label="Ciudad" value={customer?.address_city} />
          </div>
        </div>

        {/* ── Líneas ── */}
        <div>
          <p className={sectionTitleCls}>Líneas del documento</p>
          <DataTable<typeof doc.lines[number]>
            columns={[
              { key: 'sku', header: 'SKU',
                render: line => <span className="font-mono text-content-muted text-xs">{line.sku}</span> },
              { key: 'name', header: 'Nombre',
                render: line => <span className="text-content font-medium">{line.name}</span> },
              { key: 'description', header: 'Descripción',
                render: line => (
                  <span className="text-content-muted text-xs">{line.description || '—'}</span>
                ) },
              { key: 'quantity', header: 'Qty', numeric: true,
                render: line => <span className="text-content">{formatQty(line.quantity)}</span> },
              { key: 'unit_price', header: 'Precio neto', numeric: true,
                // En moneda extranjera van los DOS: el precio pactado arriba y
                // el convertido abajo. El documento se emite con el segundo y
                // el cliente reconoce el primero.
                render: line => (
                  (line as any).unit_price_original && (line as any).price_currency
                    && (line as any).price_currency !== baseCurrency ? (
                    <div className="space-y-0.5">
                      <div className="text-[11px] font-bold text-primary">
                        {(line as any).price_currency} {(line as any).unit_price_original}
                      </div>
                      <div className="text-content">{formatUnitPrice(line.unit_price)}</div>
                    </div>
                  ) : (
                    <div className="text-content">{formatUnitPrice(line.unit_price)}</div>
                  )
                ) },
              { key: 'discount_pct', header: 'Desc %', numeric: true,
                render: line => (
                  <span className="text-content-muted">
                    {line.discount_pct && parseFloat(line.discount_pct) > 0 ? `${line.discount_pct}%` : '—'}
                  </span>
                ) },
              { key: 'subtotal', header: 'Subtotal', numeric: true,
                render: line => (
                  <span className="font-semibold text-content">
                    {line.subtotal ? formatCLP(line.subtotal) : '—'}
                  </span>
                ) },
            ]}
            rows={doc.lines}
            rowKey={(_, i) => i}
            minWidth={900}
            caption="Líneas del documento"
          />

          {/* Tipos de cambio usados */}
          {doc.exchange_rate_data && Object.keys(doc.exchange_rate_data).some(k => !['locked_at', 'is_locked'].includes(k)) && (
            <div className="mt-2 flex justify-end">
              <div className="bg-primary-50 border border-primary-200 rounded-lg px-3 py-2 text-xs space-y-1">
                <p className="font-semibold text-primary-hover">
                  Tipos de cambio {((doc.exchange_rate_data.is_locked as any) === true || (doc.exchange_rate_data.is_locked as any) === 'true') ? '(bloqueados al emitir)' : '(indicativos)'}
                </p>
                <div className="space-y-1">
                  {Object.entries(doc.exchange_rate_data)
                    .filter(([k]) => !['locked_at', 'is_locked', 'lock_source'].includes(k))
                    .map(([code, rate]) => {
                      const r = Number(rate)
                      return (
                        <div key={code} className="text-primary">
                          <div>1 {code} = {formatCLP(String(rate))} CLP</div>
                          {r > 0 && Number(doc.total) > 0 && (
                            <div className="font-semibold">
                              Total: {code} {(Number(doc.total) / r).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                </div>
                {doc.exchange_rate_data.locked_at && (
                  <p className="text-[11px] text-primary-hover mt-1">Bloqueado: {doc.exchange_rate_data.locked_at}</p>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 space-y-3">
            {doc.exchange_rate_data && Object.keys(doc.exchange_rate_data).some(k => !['locked_at', 'is_locked'].includes(k)) && (
              <div className="text-right text-[11px] text-warning-content-soft italic">
                * Totales mostrados en CLP. Ver conversión en líneas arriba.
              </div>
            )}
            <div className="flex justify-end gap-6 text-sm">
              <div className="text-right">
                <p className="text-xs text-content-subtle">Subtotal</p>
                <p className="font-medium text-content">{formatCLP(doc.subtotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-content-subtle">{tax.name} ({doc.tax_rate}%)</p>
                <p className="font-medium text-content">{formatCLP(doc.tax_amount)}</p>
              </div>
              {doc.document_type === 'FC' && (
                <div className="text-right">
                  <p className="text-xs text-warning-solid">{tax.name} Retenido</p>
                  <p className="font-medium text-warning-content-soft">-{formatCLP(doc.tax_amount)}</p>
                </div>
              )}
              <div className="text-right">
                <p className="text-xs text-content-subtle">Total</p>
                <p className="text-lg font-bold text-content">{formatCLP(doc.total)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Referencias ── */}
        {doc.referencias && doc.referencias.length > 0 && (
          <div>
            <p className={sectionTitleCls}>Referencias</p>
            <DataTable<typeof doc.referencias[number]>
              columns={[
                { key: 'tipo_doc', header: 'Tipo Doc',
                  render: ref => <span className="font-mono text-content">{ref.tipo_doc}</span> },
                { key: 'folio_ref', header: 'Folio Ref',
                  render: ref => <span className="text-content">{ref.folio_ref}</span> },
                { key: 'fecha_ref', header: 'Fecha Ref',
                  render: ref => <span className="text-content-muted">{fmtDate(ref.fecha_ref, locale)}</span> },
                { key: 'cod_ref', header: 'Código',
                  render: ref => (
                    <span className="text-content-muted">
                      {ref.cod_ref ? `${ref.cod_ref} — ${COD_REF_LABELS[ref.cod_ref] || ''}` : '—'}
                    </span>
                  ) },
                { key: 'razon_ref', header: 'Razón',
                  render: ref => <span className="text-content-muted">{ref.razon_ref || '—'}</span> },
              ]}
              rows={doc.referencias}
              rowKey={(_, i) => i}
              minWidth={620}
              caption="Documentos a los que este hace referencia"
            />
          </div>
        )}

        {/* ── Notas y campos personalizados (editable, no contable) ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className={sectionTitleCls}>Notas y campos personalizados</p>
            {doc.status !== 'cancelled' && !editingMeta && (
              <button
                onClick={() => {
                  setDraftNotes(doc.notes ?? '')
                  setDraftPers(mergeCustomFields(ventasFields, doc.personalizados) as DocumentPersonalizado[])
                  setEditingMeta(true)
                }}
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover"
              >
                <Pencil size={12} />Editar
              </button>
            )}
          </div>

          {!editingMeta ? (
            <div className="space-y-3">
              {metaVisibles.length > 0 && (
                <DataTable<typeof metaVisibles[number]>
                  columns={[
                    { key: 'nombre', header: 'Nombre',
                      render: p => <span className="text-content font-medium">{p.nombre}</span> },
                    { key: 'valor', header: 'Valor',
                      render: p => (
                        <span className="text-content-muted">
                          {p.tipo === 'boolean' && p.valor
                            ? (p.valor === 'true' ? 'Sí' : 'No')
                            : (p.valor_label || p.valor || '—')}
                        </span>
                      ) },
                  ]}
                  rows={metaVisibles}
                  rowKey={(p, i) => p.nombre || i}
                  minWidth={320}
                  caption="Campos personalizados del documento"
                />
              )}
              {doc.notes ? (
                <Field label="Observaciones" value={doc.notes} />
              ) : metaVisibles.length === 0 && (
                <p className="text-xs text-content-subtle">Sin notas ni campos personalizados.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Los campos definidos por el tenant se editan con su control
                  propio (select con opciones, fecha, vínculo); los ad-hoc que
                  ya estén guardados, como texto libre. */}
              <CustomFieldsForm
                fields={ventasFields}
                value={draftPers}
                onChange={next => setDraftPers(next as DocumentPersonalizado[])}
              />
              {metaExtras.length > 0 && (
                <div className="space-y-2">
                  {metaExtras.map(p => (
                    <div key={p.nombre} className="grid grid-cols-3 gap-2 items-center">
                      <span className="text-sm text-content-muted truncate" title={p.nombre}>{p.nombre}</span>
                      <input aria-label={p.nombre}
                        value={p.valor ?? ''}
                        onChange={e => setDraftPers(arr => arr.map(x => x.nombre === p.nombre ? { ...x, valor: e.target.value } : x))}
                        className="col-span-2 px-2 py-1.5 text-sm border border-divider rounded-lg bg-input-bg text-content"
                        placeholder={`Valor de ${p.nombre}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              <TextField
                dense
                label="Observaciones"
                multiline
                rows={3}
                value={draftNotes}
                onChange={e => setDraftNotes(e.target.value)}
                placeholder="Notas u observaciones internas…"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingMeta(false)} className="px-3 py-1.5 text-xs border border-divider rounded-lg text-content-muted hover:bg-soft">Cancelar</button>
                <button
                  onClick={() => updateMeta.mutate(
                    { personalizados: draftPers, notes: draftNotes },
                    { onSuccess: () => setEditingMeta(false) },
                  )}
                  disabled={updateMeta.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
                >
                  {updateMeta.isPending && <Loader2 size={12} className="animate-spin" />}Guardar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Pagos Asociados ── */}
        {doc.payments && doc.payments.length > 0 && (
          <div>
            <p className={sectionTitleCls}>Pagos Asociados</p>
            <DataTable<typeof doc.payments[number]>
              columns={[
                { key: 'payment_date', header: 'Fecha', sortable: true,
                  render: p => <span className="text-content">{fmtDate(p.payment_date, locale)}</span> },
                { key: 'payment_method_display', header: 'Método', sortable: true,
                  render: p => <span className="text-content-muted">{p.payment_method_display}</span> },
                { key: 'bank_account_display', header: 'Banco', sortable: true,
                  render: p => <span className="text-content-muted">{p.bank_account_display || '—'}</span> },
                { key: 'reference', header: 'Referencia', sortable: true,
                  render: p => <span className="text-content-muted">{p.reference || '—'}</span> },
                { key: 'payment_status', header: 'Estado', sortable: true,
                  render: p => (
                    <StatusBadge status={p.payment_status} catalog={PAYMENT_STATUS} dot={false} />
                  ) },
                { key: 'amount', header: 'Aplicado', numeric: true, sortable: true,
                  sortValue: p => Number(p.amount),
                  render: p => <span className="font-medium text-content">{formatCLP(p.amount)}</span> },
                { key: 'payment_total', header: 'Total Pago', numeric: true, sortable: true,
                  sortValue: p => Number(p.payment_total),
                  render: p => <span className="text-content-muted">{formatCLP(p.payment_total)}</span> },
              ]}
              rows={doc.payments}
              rowKey={p => p.id}
              minWidth={840}
              caption="Pagos recibidos contra este documento"
            />
            {(() => {
              const totalPaid = doc.payments
                .filter(p => p.payment_status !== 'cancelled')
                .reduce((s, p) => s + parseFloat(p.amount || '0'), 0)
              const docTotal = parseFloat(doc.total || '0')
              const remaining = docTotal - totalPaid
              return (
                <div className="mt-3 flex justify-end gap-6 text-sm">
                  <div className="text-right">
                    <p className="text-xs text-content-subtle">Total cobrado</p>
                    <p className="font-medium text-success-content-soft">{formatCLP(String(totalPaid))}</p>
                  </div>
                  {remaining > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-content-subtle">Pendiente</p>
                      <p className="font-semibold text-content">{formatCLP(String(remaining))}</p>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        )}

        {/* ── Historial de Emisión (colapsable) ── */}
        {doc.emission_logs && doc.emission_logs.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setEmissionLogsOpen(v => !v)}
              className="flex items-center gap-2 w-full text-left"
            >
              {emissionLogsOpen ? <ChevronDown size={14} className="text-content-subtle" /> : <ChevronRight size={14} className="text-content-subtle" />}
              <p className={sectionTitleCls}>Historial de Emisión ({doc.emission_logs.length})</p>
            </button>
            {emissionLogsOpen && <div className="space-y-2 mt-2">
              {[...doc.emission_logs].reverse().map(log => (
                <div key={log.id} className="flex items-start gap-2.5 p-3 rounded-lg border border-divider bg-glass-3">
                  <LogIcon status={log.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-content">
                        {EVENT_LABELS[log.event] || log.event}
                      </p>
                      <p className="text-xs text-content-subtle shrink-0">
                        {fmtDateTime(log.created_at, locale)}
                      </p>
                    </div>
                    {log.message && (
                      <p className="text-xs text-content-muted mt-0.5">{log.message}</p>
                    )}
                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      log.event === 'email_sent' ? (
                        <div className="text-xs text-content-muted mt-1 space-y-0.5">
                          {(() => { const m = log.metadata as Record<string, any>; return <>
                            <p>Para: {(m.to || []).join(', ')}</p>
                            {m.cc?.length > 0 && <p>CC: {m.cc.join(', ')}</p>}
                            <p>Adjuntos: {(m.attachments || []).join(', ') || 'Ninguno'}</p>
                          </> })()}
                        </div>
                      ) : (
                        <p className="text-xs text-content-subtle font-mono mt-0.5">
                          {JSON.stringify(log.metadata)}
                        </p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>}
          </div>
        )}

        {/* ── Comprobante Contable (Preview) ── */}
        <div>
          <button
            type="button"
            onClick={() => setVoucherOpen(v => !v)}
            className="flex items-center gap-2 w-full text-left"
          >
            {voucherOpen ? <ChevronDown size={14} className="text-content-subtle" /> : <ChevronRight size={14} className="text-content-subtle" />}
            <p className={sectionTitleCls + ' mb-0'}>Comprobante Contable</p>
          </button>
          {voucherOpen && (
            <div className="mt-3">
              {isLoadingVoucher ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : voucherPreview?.warning ? (
                <div className="bg-warning-surface border border-warning-border rounded-lg p-3">
                  <p className="text-xs text-warning-content">{voucherPreview.warning}</p>
                </div>
              ) : voucherPreview?.lines && voucherPreview.lines.length > 0 ? (
                <>
                  <DataTable<typeof voucherPreview.lines[number]>
                    columns={[
                      { key: 'account_code', header: 'Cuenta',
                        render: ln => (
                          <>
                            <span className="font-mono text-xs text-content-muted">{ln.account_code}</span>
                            <span className="text-xs text-content-muted ml-1">{ln.account_name}</span>
                            {ln.cost_center_code && (
                              <span className="ml-1 text-[10px] text-info-content-soft bg-info-surface px-1 rounded">
                                {ln.cost_center_code}
                              </span>
                            )}
                          </>
                        ) },
                      { key: 'glosa', header: 'Glosa',
                        render: ln => <span className="text-xs text-content-muted">{ln.glosa}</span> },
                      { key: 'debe', header: 'Debe', numeric: true,
                        render: ln => (
                          <span className="text-xs text-content">
                            {parseFloat(ln.debe) > 0 ? formatCLP(ln.debe) : '—'}
                          </span>
                        ) },
                      { key: 'haber', header: 'Haber', numeric: true,
                        render: ln => (
                          <span className="text-xs text-content">
                            {parseFloat(ln.haber) > 0 ? formatCLP(ln.haber) : '—'}
                          </span>
                        ) },
                    ]}
                    rows={voucherPreview.lines}
                    rowKey={(_, i) => i}
                    minWidth={560}
                    footerLabel="Total"
                    footer={{
                      debe: formatCLP(voucherPreview.totals.total_debe),
                      haber: formatCLP(voucherPreview.totals.total_haber),
                    }}
                    caption="Asiento contable que genera este documento"
                  />
                </>
              ) : (
                <p className="text-xs text-content-subtle italic">Sin datos de preview contable.</p>
              )}
            </div>
          )}
        </div>

        {/* ── Adjuntos propios del documento ── */}
        {doc.attachments && doc.attachments.length > 0 && (
          <div>
            <p className={sectionTitleCls}>Adjuntos del Documento ({doc.attachments.length})</p>
            <div className="space-y-1.5">
              {doc.attachments.map((att: any) => (
                <div key={att.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-divider hover:bg-soft transition-colors">
                  <Paperclip size={13} className="text-content-subtle flex-shrink-0" />
                  <span className="text-xs text-content flex-1 truncate">{att.file_name}</span>
                  <span className="text-[10px] text-content-subtle">{(att.file_size / 1024).toFixed(1)} KB</span>
                  <a href={att.download_url} target="_blank" rel="noreferrer"
                    className="p-1 text-content-subtle hover:text-primary transition-colors" title="Ver">
                    <Eye size={13} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Adjuntos del pedido de origen (solo lectura) ── */}
        {doc.order_attachments && doc.order_attachments.length > 0 && (
          <div>
            <p className={sectionTitleCls}>Adjuntos del Pedido ({doc.order_attachments.length})</p>
            <div className="space-y-1.5">
              {doc.order_attachments.map((att: any) => (
                <div key={att.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-divider bg-row-alt/50 hover:bg-soft transition-colors">
                  <Paperclip size={13} className="text-content-subtle flex-shrink-0" />
                  <span className="text-xs text-content-muted flex-1 truncate">{att.file_name}</span>
                  <span className="text-[10px] text-content-subtle">{(att.file_size / 1024).toFixed(1)} KB</span>
                  <a href={att.download_url} target="_blank" rel="noreferrer"
                    className="p-1 text-content-subtle hover:text-primary transition-colors" title="Ver">
                    <Eye size={13} />
                  </a>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-content-subtle italic">Adjuntos del pedido de origen. Administra desde el pedido.</p>
          </div>
        )}

        {/* ── XML del Documento ── */}
        {(() => {
          const ld = doc.localization_data as Record<string, unknown> | undefined
          const hasUnsigned = !!(ld?.xml_unsigned || ld?.xml_unsigned_s3_key)
          const hasSigned = !!(ld?.xml_signed || ld?.xml_signed_s3_key)
          if (!hasUnsigned && !hasSigned) return null
          const folioLabel = doc.folio ? `${doc.document_type}-${doc.folio}` : `${doc.document_type}-${doc.id}`
          const variants: XmlVariant[] = []
          if (hasUnsigned) {
            variants.push({
              key: 'unsigned',
              label: 'XML Sin Firmar',
              filename: `${folioLabel}_sin_firmar.xml`,
              fetcher: async () => {
                const res = await apiClient.get(`/sales/documents/${doc.id}/xml/?variant=unsigned`, { responseType: 'blob' })
                return await (res.data as Blob).text()
              },
            })
          }
          if (hasSigned) {
            variants.push({
              key: 'signed',
              label: 'XML Firmado',
              filename: `${folioLabel}_firmado.xml`,
              fetcher: async () => {
                const res = await apiClient.get(`/sales/documents/${doc.id}/xml/?variant=signed`, { responseType: 'blob' })
                return await (res.data as Blob).text()
              },
            })
          }
          return <XmlViewer variants={variants} />
        })()}

      </div>
    </Modal>
  )
}
